import React, { useEffect, useState } from 'react';
import Header from '../header/Header';
import Content from '../content/Content';
import Footer from '../footer/Footer';
import { Stack } from '@mui/material';
import AlertComponent from '../alert/Alert';
import { AirlineType, Alert, PrinterType } from '../../enums/enum';
import {
  briefingPacketOverrideMsg,
  validityMessageCreator,
} from '../../utils/messageCreator';
import { dataEmptyChecker } from '../../utils/validators';
import { dateFormatter } from '../../utils/dateHandler';
import { useAuth } from '../context/AuthContext/AuthContext';
import ContentSkeleton from '../skeleton/ContentSkeleton';
import { useAppInsightConsumer } from '../context/AppInsightContext/AppInsightContext';
import {
  createErrorTelemetryProperties,
  getErrorMessage,
} from '../../utils/appInsightUtils';
import { AxiosError } from 'axios';
import {
  appStateActions,
  useAppDispatch,
  useAppSelector,
} from '../../store/store';
import useRequestAndPrintPacket from '../../customHooks/useRequestAndPrintPacket/useRequestAndPrintPacket';
import { PacketRequestType } from '../../@types/PacketReqFormData.type';

const Home: React.FC = () => {
  const { trackPageView, logInformation, logError, trackError } =
    useAppInsightConsumer();

  trackPageView('Home Page');

  // This state determines whether and what to show in the Alert component with Validation message
  const [showValidityMsg, setShowValidityMsg] = useState<{
    status: boolean;
    message: string;
  }>({
    status: false,
    message: '',
  });

  const [psid, setPsid] = useState<string | undefined>(undefined);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [weatherOverrideMsg, setWeatherOverrideMsg] = useState<string>('');

  const {
    error,
    isPending,
    isPaused,
    isError,
    isSuccess,
    mutate: reqBriefingPacket,
  } = useRequestAndPrintPacket();

  const dispatch = useAppDispatch();

  const {
    errorState,
    airlineTypeData,
    printerTypeData,
    selectStationData,
    selectPrinterData,
    networkPath,
    packetTypeData,
    flightOrigin,
    flightNumber,
    flightDate,
    weatherOverideAirlineData,
  } = useAppSelector((state) => state.appStateReducer);

  const { isLoading, userData } = useAuth();

  const isLaserOutput = printerTypeData === PrinterType.LASER;

  // Request summary used in App Insights logs.
  const describePacketRequest = () => `{
              psid: ${psid},
              airline: ${airlineTypeData},
              printerOutputType: ${isLaserOutput ? 'PRINTER' : 'FILE'},
              packetType: ${packetTypeData},
              outputPrinterCode: ${isLaserOutput ? selectPrinterData : 'FILE'},
              fltDate: ${dateFormatter(flightDate)},
              fltNum: ${(flightNumber as string)?.trim()},
              fltOrig: ${(flightOrigin as string)?.trim()?.toUpperCase()},
              ${isLaserOutput ? 'networkPath: ' + networkPath : ''}
            }`;

  const buildPacketRequest = (): PacketRequestType => ({
    psid: psid ?? 'XXXXXXX',
    airline: airlineTypeData?.trim()?.toUpperCase(),
    printerOutputType: isLaserOutput ? 'PRINTER' : PrinterType.FILE,
    outputPrinterCode: isLaserOutput
      ? (selectPrinterData as string)
      : PrinterType.FILE,
    packetType: packetTypeData,
    fltDate: dateFormatter(flightDate),
    fltNum: (flightNumber as string)?.trim(),
    fltOrigin: (flightOrigin as string)?.trim()?.toUpperCase(),
    ...(isLaserOutput ? { networkPath } : {}),
  });

  // Validates every field and shares the result with the fields through the global state.
  const validateForm = () => {
    const isFileOutput = printerTypeData === PrinterType.FILE;
    const isSelectStationValid =
      isFileOutput || !dataEmptyChecker(selectStationData);
    // The printer is only validated once a station has been selected.
    const isSelectPrinterValid =
      !isSelectStationValid ||
      isFileOutput ||
      !dataEmptyChecker(selectPrinterData);

    const fields = {
      airlineType: !dataEmptyChecker(airlineTypeData),
      printerType: !dataEmptyChecker(printerTypeData),
      selectStation: isSelectStationValid,
      selectPrinter: isSelectPrinterValid,
      packetType: !dataEmptyChecker(packetTypeData),
      flightOrigin: !dataEmptyChecker(flightOrigin),
      flightNumber: !dataEmptyChecker(flightNumber),
      flightDate: !dataEmptyChecker(flightDate),
    };

    dispatch(appStateActions.updateValidityStatus(fields));

    return {
      fields,
      isValid: Object.values(fields).every(Boolean),
    };
  };

  const logMissingPsid = () => {
    logError(
      getErrorMessage(
        new Error(
          `No PSID retrieved from Fedauth / Ping dentity for user ${userData?.profile?.email}. Checking for Arctic ID`,
        ),
      ) as string,
      'Home',
      'useEffect',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      createErrorTelemetryProperties(
        new Error(
          `No PSID retrieved from Fedauth / Ping dentity for user ${userData?.profile?.email}`,
        ),
      ),
    );
    trackError(
      new Error(
        `No PSID retrieved from Fedauth / Ping dentity for user ${userData?.profile?.email}`,
      ),
    );
  };

  const submitValidRequest = () => {
    // AppInsight Logging
    logInformation(
      `User ${userData?.profile?.email} has passed the validation and the validation alert will be 
          removed if any`,
      'Home',
      'submitJetStreamReq',
    );

    setShowValidityMsg({
      status: false,
      message: '',
    });

    // PSID cannot be retrieved from Fedauth / Ping dentity
    if (!psid) {
      logMissingPsid();
    }

    // AppInsight Logging
    logInformation(
      `User ${userData?.profile?.email} is trying to submit the request with 
            printer type ${printerTypeData} and have the following parameters as request 
            ${describePacketRequest()}`,
      'Home',
      'submitJetStreamReq',
    );

    reqBriefingPacket({
      reqData: buildPacketRequest(),
      accessToken: userData?.access_token as string,
    });
  };

  const showValidationErrors = (
    fields: ReturnType<typeof validateForm>['fields'],
  ) => {
    const message = validityMessageCreator(
      fields.airlineType,
      fields.printerType,
      fields.selectStation,
      fields.selectPrinter,
      fields.packetType,
      fields.flightOrigin,
      fields.flightNumber,
      fields.flightDate,
    );
    // AppInsight Logging
    logInformation(
      `Validation didn't pass for user ${userData?.profile?.email} and the message shown: ${message}`,
      'Home',
      'submitJetStreamReq',
    );

    // AppInsight Logging
    logInformation(
      `Validation Result Fieldwise for ${userData?.profile?.email} is: 
          {
            AirlineType(AS/QX): ${fields.airlineType},
            PrinterType(LASER/FILE): ${fields.printerType},
            SelectedStationValid: ${fields.selectStation},
            SelectedPrinterValid: ${fields.selectPrinter},
            PacketTypeValid: ${fields.packetType},
            FlightOriginValid: ${fields.flightOrigin},
            FlightNumberValid: ${fields.flightNumber},
            FlightDateValid(Must be yesterday, today and tom): ${fields.flightDate},
          }`,

      'Home',
      'submitJetStreamReq',
    );

    setShowValidityMsg({
      status: true,
      message: message,
    });
  };

  // This function and validates the form data and then calls the API
  const submitJetStreamReq = () => {
    setShowValidityMsg({
      status: false,
      message: '',
    });

    setShowSuccessAlert(false);
    dispatch(appStateActions.updateErrorState(undefined));

    try {
      // AppInsight Logging
      logInformation(
        `User ${userData?.profile?.email} Requested for packet`,
        'Home',
        'submitJetStreamReq',
      );

      // If any user removes the disabled attribute from the button by using the browser dev tools
      // they would be able to submit the request even if the airlineType data matches with the
      // airline from the weather Override Api. This condition will not allow to do so.
      if (airlineTypeData === weatherOverideAirlineData) {
        // AppInsight Logging
        logInformation(
          `Weather Override Airline Data is on for ${airlineTypeData}. Still ${userData?.profile?.email} 
        from airline ${userData?.profile?.company} trying to submit request. And it was handled by the code.`,
          'Home',
          'submitJetStreamReq',
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // AppInsight Logging
      logInformation(
        `Validation of the formdata starting for ${userData?.profile?.email}`,
        'Home',
        'submitJetStreamReq',
      );

      const { fields, isValid } = validateForm();

      // AppInsight Logging
      logInformation(
        `Validation of the formdata completed for user ${userData?.profile?.email}`,
        'Home',
        'submitJetStreamReq',
      );

      if (isValid) {
        submitValidRequest();
      } else {
        // Don't call the API; share the updated validation message instead.
        showValidationErrors(fields);
      }
    } catch (err) {
      // AppInsight Logging
      logError(
        getErrorMessage(err as Error | AxiosError) as string,
        'Home',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(err),
      );

      // AppInsight Error Tracking
      trackError(err as Error | AxiosError);

      dispatch(appStateActions.updateErrorState(err as Error));
    }
  };
  // On Authenticaion check from which airline user belongs from and accordingly update the
  // airlineType and also tyhe arcticID for the user
  useEffect(() => {
    if (!isLoading && userData) {
      try {
        // Update the Airline Type
        const airline = userData?.profile?.company as string;

        // In case the company data is undefined or null default it to AS
        dispatch(
          appStateActions.updateAirlineTypeData(airline || AirlineType.AS),
        );

        const psidData = userData?.profile?.emplid; //peoplesoftid

        // AppInsight Logging

        setPsid(psidData as string | undefined);
      } catch (err) {
        // AppInsight Logging
        logError(
          getErrorMessage(
            new Error(
              `Issue while retrieving the psid for user ${userData?.profile?.email}, PSID : ${psid}`,
            ),
          ) as string,
          'Home',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(
            new Error(
              `Issue while retrieving the arcticid and psid for user ${userData?.profile?.email}, PSID : ${psid}`,
            ),
          ),
        );
        trackError(
          new Error(
            `Issue while retrieving the arcticid and psid for user ${userData?.profile?.email}`,
          ),
        );

        logError(
          getErrorMessage(err as Error | AxiosError) as string,
          'Home',
          'useEffect',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          createErrorTelemetryProperties(err),
        );

        // AppInsight Error Tracking
        trackError(err as Error | AxiosError);

        // This error might not come up or come and go away as the errorState will be made undefined by
        // station Api
        dispatch(appStateActions.updateErrorState(err as Error));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, userData]);

  // If the error state is updated then remove the validation message from the screen. Because I want
  // the user to get focussed on the most updated error coming up. This will be helpful for troubleshooting
  useEffect(() => {
    if (errorState) {
      dispatch(appStateActions.handlePacketReqLoading(false));
      setShowValidityMsg({
        status: false,
        message: '',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorState]);

  // Whenever weatherOverrideAirlineData is updated then check if the airlineTypeData is same as that of
  // it. If yes then show the relevant message in the Alert component and also reset all the validation
  // errors and messages
  useEffect(() => {
    if (airlineTypeData === weatherOverideAirlineData) {
      // AppInsight Logging
      logInformation(
        `Weather Override Airline Data is on for ${airlineTypeData} requested by ${userData?.profile?.email}.`,
        'Home',
        'useEffect',
      );
      // Resetting Validation Error
      dispatch(appStateActions.resetAllValidationErrors());

      // Resetting Validation Message and status
      setShowValidityMsg({
        status: false,
        message: '',
      });

      // Retrieving the Override message that needs to be shown
      const message = briefingPacketOverrideMsg(weatherOverideAirlineData);

      // Update the state with the message created
      setWeatherOverrideMsg(message);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weatherOverideAirlineData]);

  // Handle Network Failure and 5xx / 4xx response from the API
  useEffect(() => {
    if (isError && !isPending) {
      // AppInsight Logging
      logError(
        getErrorMessage(
          new Error(`Got error from Jetstream api for 
            ${userData?.profile?.email} with the following parameters as request : 
            ${describePacketRequest()} for user ${userData?.profile?.email}`),
        ) as string,
        'Home',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(
          new Error(`Got error from Jetstream Api for 
            ${userData?.profile?.email} with the following parameters as request : 
            ${describePacketRequest()} for user ${userData?.profile?.email}`),
        ),
      );
      trackError(
        new Error(`Got error from Jetstream api for 
          ${userData?.profile?.email} with the following parameters as request :
            ${describePacketRequest()} for user ${userData?.profile?.email}`),
      );

      logError(
        getErrorMessage(error) as string,
        'Home',
        'useEffect',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        createErrorTelemetryProperties(error),
      );
      trackError(error);

      dispatch(appStateActions.updateErrorState(error));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isPending]);

  useEffect(
    () => {
      if (isPaused && !isPending) {
        setShowValidityMsg({
          status: false,
          message: '',
        });

        setShowSuccessAlert(false);

        // AppInsight Logging
        logInformation(
          `Request is paused for user ${userData?.profile?.email}`,
          'Home',
          'useEffect',
        );

        dispatch(
          appStateActions.updateErrorState(
            new Error('Please check your network connection and try again'),
          ),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPaused, isPending],
  );

  // Handle the API response when successfull
  useEffect(() => {
    if (isSuccess && !isPending) {
      // If the packet API is successfull then reset the error state. This helps to clear any previous
      // error
      dispatch(appStateActions.updateErrorState(undefined));
      setShowSuccessAlert(true);

      // AppInsight Logging
      logInformation(
        `Response from Jetstream Api endpoint came up successfully for 
          user ${userData?.profile?.email}`,
        'Home',
        'useEffect',
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isPending]);

  // Handle the Pending / Loading State of the API
  useEffect(() => {
    // When loading state is true
    if (isPending) {
      setShowValidityMsg({
        status: false,
        message: '',
      });

      setShowSuccessAlert(false);

      dispatch(appStateActions.updateErrorState(undefined));

      // Update the global loading state wrt to the packet Reques Api to true
      dispatch(appStateActions.handlePacketReqLoading(isPending));
    }
    // When loading state is false
    else {
      // Update the global loading state wrt to the packet Reques Api to false
      dispatch(appStateActions.handlePacketReqLoading(isPending));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  // Check is the Authenticaion on

  // If the user is authenticated and the data is loaded then show the fields
  if (userData && !isLoading) {
    return (
      <Stack justifyContent="space-between" height="100vh">
        {/* Header */}
        <Header />

        {/* Show error Alert */}
        {errorState && (
          <AlertComponent
            type={Alert.ERROR}
            message={errorState?.message ?? 'Errored out'}
            error={errorState}
          />
        )}

        {/* Show success Alert */}
        {showSuccessAlert && <AlertComponent type={Alert.SUCCESS} />}

        {/* Show Validation Error */}
        {airlineTypeData === weatherOverideAirlineData && (
          <AlertComponent
            type={Alert.WEATHEROVERRIDEERROR}
            message={weatherOverrideMsg}
          />
        )}

        {/* Show Validation Alert */}
        {showValidityMsg.status &&
          airlineTypeData !== weatherOverideAirlineData && (
            <AlertComponent
              type={Alert.WARNING}
              // If somehow things go undefined there should not be processung errors
              message={showValidityMsg?.message || ''}
            />
          )}

        <Content />

        {/* Pass the request submit function to the Footer as on clicking on the Request Packet Button in the 
      Footer the submitJetStreamReq function will be trigerred */}
        <Footer handleSubmit={submitJetStreamReq} />
      </Stack>
    );
  }

  // Else show the Skeleton Component
  return <ContentSkeleton />;
};

export default Home;
